/**
 * useOptimizeActions — the solver orchestration behind Draft Plan's buttons.
 *
 * Extracted from DraftPlan.jsx, which was doing two jobs: composing a page and
 * running a multi-rank optimizer over two objectives with a shape comparison on
 * top. This file owns the second job. DraftPlan composes and calls in.
 *
 * Everything here is about turning solver output into PLANS — building the
 * candidate pool, pricing it, running the ranks, deciding whether the two
 * objectives actually disagree, and writing the result. No rendering.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { optimizePlan, optimizePlans, lockedPlanOf, lockedSlotIds, lockedSpendOf } from "../../utils/planOptimizer";
import { rosterablePositions } from "../../utils/rosterSlotFill";
import { UNIQUE_PLAYERS } from "../../data/players";
import { BUCKET_WIDTH, primaryOf, planObjective, planBudgetMode } from "../../hooks/usePlanState";
import { planFingerprint } from "../../utils/planFingerprint";
import { makePlayerPpg, makePlayerSeasonPoints, makePlayerVorp } from "./ppgTotals";
import { getPlayerByKey } from "../../utils/playerIndex";
import { capturePlanOptimized } from "../../utils/planAnalytics";

/**
 * 🔴 WHY THE TWO OBJECTIVES SO OFTEN AGREE, AND WHY SILENCE ABOUT IT READS AS
 * A BUG.
 *
 * With position counts held fixed, Σ VORP = Σ proj − Σ replacement(pos), and
 * that second term is a constant — so PPG and VORP are the SAME objective up to
 * an additive constant whenever both builds take the same positional shape.
 * They can only diverge where the solver is free to CHANGE that shape, which in
 * practice means the flex and superflex slots.
 *
 * So flipping the toggle will often produce an identical team. That is a
 * property of the mathematics, not a broken switch — but a toggle that visibly
 * does nothing is indistinguishable from one that is wired wrong, so the
 * agreement gets said out loud.
 *
 * 🔴 AND WHEN THEY DIVERGE, NAME THE SWAP. "VORP takes 2 more RB" is a count,
 * and a count invites the reading that VORP found a BETTER team; it did not, it
 * found a different one worth about the same. "VORP would take Bijan Robinson
 * over Ja'Marr Chase" is the actual decision, in the only terms anybody can act
 * on — two names.
 */
const keyOfAssignment = (a) => `${a.player.name}-${a.player.pos}`;

/**
 * How the two builds differ, as a sentence.
 *
 * The pairing of "in" against "out" is a PRESENTATION choice and nothing more —
 * there is no true correspondence between two independently optimal rosters, so
 * both sides are sorted by price and read off the top. That makes the named
 * swap the largest-dollar one, which is the one worth a sentence.
 */
export function agreementNote(mine, theirs, mineLabel, theirLabel) {
  const mineKeys = new Set((mine || []).map(keyOfAssignment));
  const theirKeys = new Set((theirs || []).map(keyOfAssignment));
  const byPrice = (x, y) => y.price - x.price;
  const gained = (theirs || []).filter((a) => !mineKeys.has(keyOfAssignment(a))).sort(byPrice);
  const lost = (mine || []).filter((a) => !theirKeys.has(keyOfAssignment(a))).sort(byPrice);

  if (gained.length === 0 && lost.length === 0) {
    return `${mineLabel} and ${theirLabel} agree on this roster.`;
  }

  // Both builds fill the same slots, so these are the same length in practice.
  // The guards are for the degenerate case where one chain came back short.
  const pairsCount = Math.max(gained.length, lost.length);
  const rest = pairsCount - 1;
  const tail = rest > 0 ? ` and ${rest} other change${rest === 1 ? "" : "s"}` : "";

  if (gained.length === 0) return `${theirLabel} would drop ${lost[0].player.name}${tail}.`;
  if (lost.length === 0) return `${theirLabel} would add ${gained[0].player.name}${tail}.`;
  return `${theirLabel} would take ${gained[0].player.name} over ${lost[0].player.name}${tail}.`;
}

/** The slots where the two builds put different players. */
function differingSlots(mine, theirs) {
  const bySlot = (list) => {
    const m = {};
    (list || []).forEach((a) => { m[a.slotId] = keyOfAssignment(a); });
    return m;
  };
  const a = bySlot(mine);
  const b = bySlot(theirs);
  return Object.keys(a).filter((id) => a[id] !== b[id]);
}

/**
 * 🔴 WHAT AN ABSENCE LOOKS LIKE. A from-scratch solve can DROP a player you
 * committed to, and that is the whole point of the mode — but a disappearance
 * is the one kind of change nobody spots comparing two 16-row grids by eye. The
 * player is simply not there, and there is no row that says so.
 *
 * So the finding is reported as a sentence: "Dak Prescott at $10 drops out;
 * Jared Goff at $2 replaces him."
 */
export function changeNote(plan, assignments, rosterSlots) {
  const buckets = (plan && plan.buckets) || {};
  const after = {};
  (assignments || []).forEach((a) => { after[a.slotId] = a; });

  const drops = [];
  (rosterSlots || []).forEach((slot) => {
    const before = primaryOf(buckets[slot.id]);
    if (!before) return;
    const now = after[slot.id];
    if (now && `${now.player.name}-${now.player.pos}` === before.key) return;   // kept
    drops.push({
      out: before,
      in: now || null,
    });
  });
  if (drops.length === 0) return "Every player you committed to survives the re-solve at your prices.";

  const phrase = ({ out, in: got }) => (got
    ? `${out.name} at $${Math.round(out.price)} drops out; ${got.player.name} at $${Math.round(got.price)} replaces him`
    : `${out.name} at $${Math.round(out.price)} drops out`);

  // Cap the sentence rather than listing sixteen: the first few carry the point
  // and the grid carries the rest.
  const shown = drops.slice(0, 3).map(phrase);
  const rest = drops.length - shown.length;
  return `${shown.join("; ")}${rest > 0 ? `, and ${rest} more change${rest === 1 ? "" : "s"}` : ""}.`;
}

/**
 * The scope word a generated plan wears, e.g. "Plan C (PPG, rebuilt)".
 *
 * 🔴 IT REPLACED " at my prices", WHICH WAS FACTUALLY WRONG. makePriceOf reads
 * the price sheet in EVERY mode, so that phrase distinguished nothing — it
 * named a property all three modes share. Objective and scope are the two axes
 * that actually differ, and they are exactly the two the toolbar now separates.
 */
const SCOPE_WORD = { remaining: "filled", scratch: "rebuilt", commitments: "kept" };

/** The eligible candidate pool for a league, filtered to rosterable positions. */
export function poolFor(settings) {
  // 🔴 Without this a no-DEF league gets a defense parked on a bench slot — the
  // named "BN5 Houston Texans $1" bug rosterablePositions exists to prevent.
  const allowed = rosterablePositions(settings ? settings.positions : {});
  return UNIQUE_PLAYERS.filter((p) => allowed.has(p.pos));
}

/**
 * priceOf: your number when you have set one, the board's otherwise.
 *
 * 🔴 IT READS THE SHEET, NOT THE PLAN. That is the difference between "price
 * the players I have already placed" and "price anyone". Every candidate the
 * solver considers — in a plan or not — is costed at whatever you said, so you
 * can price CMC at $30 without placing him and watch the optimizer go and get
 * him. Reading edited targets instead could only ever reprice players that were
 * already in the plan, which is the limitation the sheet exists to remove.
 */
export function makePriceOf(dynamicPrices, sheet) {
  const own = sheet || {};
  return (p) => {
    const key = `${p.name}-${p.pos}`;
    if (own[key] !== undefined) return own[key];
    return dynamicPrices ? (dynamicPrices.get(key) ?? 1) : 1;
  };
}

// Cost-of-commitment is one solve per committed slot plus a baseline. Each is
// ~7ms, so a full roster is ~120ms — fine on a pause, absurd per keystroke.
const COST_DEBOUNCE_MS = 400;

/** Anything at or below this reads as "already optimal" and is not reported. */
export const COST_EPSILON = 0.05;

/**
 * COST OF COMMITMENT, per slot: what does holding THIS player cost, on its own,
 * measured in starters PPG?
 *
 * 🔴 MEASURED AGAINST THE FULLY-FREE OPTIMUM, not against the build that holds
 * all your commitments. This is the load-bearing decision in the whole
 * calculation. Against the all-held build every commitment costs zero BY
 * CONSTRUCTION — that build already contains them, so removing one and
 * re-solving just puts it back. Zero everywhere looks like a working feature
 * and means nothing. So: solve once with nothing committed (the ceiling), then
 * once per committed slot with THAT player forced in and everything else free.
 * The gap is what that one decision costs.
 *
 * 🔴 AND AT BOARD PRICES FOR EVERYONE ELSE. It answers "what would this cost if
 * the rest of the market behaved", not "what will this cost".
 *
 * Pure and exported so it can be pinned: it had no tests, and each of its three
 * behaviours (zero-suppression, slot-identity independence, and the baseline
 * choice above) is a distinct way for the number to be quietly wrong.
 *
 * @returns {{ [slotId: string]: number }} costs above COST_EPSILON only.
 */
export function commitmentCostsFor({ plan, rosterSlots, pool, priceOf, valueOf, ppgOf }) {
  const buckets = (plan && plan.buckets) || {};
  // 🔴 A LOCKED SLOT GETS NO COST BADGE, and it is not an omission. The badge
  // answers "what is this DECISION costing you" — a question that only has a
  // point while the decision is still open. You cannot un-buy a player, so
  // pricing his slot at 1.8 PPG is a number nobody can act on, sitting on the
  // one card where it reads as a reproach.
  const committed = (rosterSlots || [])
    .map((slot) => ({ slot, primary: primaryOf(buckets[slot.id]) }))
    .filter((x) => x.primary && !x.primary.locked);
  if (committed.length === 0) return {};

  const budget = plan.budget;
  // 🔴 AND THE CEILING ITSELF HOLDS THE LOCKS. The baseline is "the best roster
  // still available to you", so a free solve that spends your locked players'
  // money again would be an unreachable ceiling, and every remaining commitment
  // would be charged for the difference. That inflates every badge on the page
  // by roughly what you have already spent.
  const held = lockedPlanOf(plan);
  const benchIds = new Set(rosterSlots.filter((s) => s.pos === "BN").map((s) => s.id));
  const startersOf = (assignments) => assignments.reduce(
    (sum, a) => (benchIds.has(a.slotId) ? sum : sum + (Number(ppgOf(a.player)) || 0)), 0
  );

  const free = optimizePlan({ plan: held, rosterSlots, pool, priceOf, valueOf, budget });
  if (!free.ok) return {};
  const ceiling = startersOf(free.assignments);

  const out = {};
  committed.forEach(({ slot, primary }) => {
    // Only this one player held; every other slot free. That isolation is what
    // makes the figure attributable to this commitment rather than to the
    // interaction of all of them.
    const withThisOne = optimizePlan({
      plan: {
        buckets: { ...held.buckets, [slot.id]: { budget: null, cells: [primary, null, null, null] } },
      },
      rosterSlots, pool, priceOf, valueOf, budget,
    });
    if (!withThisOne.ok) return;
    // The held player is not in `assignments` (his slot was never open), so his
    // own contribution has to be added back — unless he is on the bench, where
    // he contributes nothing to a starters total either way.
    // 🔴 The primary is a TARGET (key/name/pos/team/price), not a player
    // record — while every other call here values a real player off the pool.
    // Resolving it back keeps one shape flowing into ppgOf. It matters beyond
    // tidiness: a Target carries no posRank, which DEF and K projections read,
    // and a silently-zero `own` inflates that slot's cost by exactly the
    // player's own PPG. Same resolve-then-fall-back pattern TargetCell uses.
    const record = getPlayerByKey(primary.key) || primary;
    const own = benchIds.has(slot.id) ? 0 : (Number(ppgOf(record)) || 0);
    const cost = ceiling - (startersOf(withThisOne.assignments) + own);
    // Zero means the commitment is already optimal. Report NOTHING rather than
    // a zero — "costs 0.0 PPG" on screen and no badge are different bugs, and
    // the second is the correct one. Negatives are float noise: the free solve
    // is the ceiling, so nothing can legitimately beat it.
    if (cost > COST_EPSILON) out[slot.id] = cost;
  });
  return out;
}

const money = (n) => `$${Math.round(n)}`;

/**
 * Can this plan be solved AT ALL with the locks it holds? Run BEFORE the
 * solver, so a refusal leaves the plan untouched.
 *
 * 🔴 THE SOLVER'S OWN MESSAGES CANNOT SAY THIS. optimizePlan reports "$3 left
 * cannot fill 11 open slots" — true, and it names neither the locks nor the
 * overage, so the one fact that explains the number is the one fact missing.
 * A person who has just locked their fifth player at a live auction needs to be
 * told they are $14 over across five locks, not handed arithmetic about what is
 * left.
 *
 * 🔴 AND IT IS GATED ON HAVING LOCKS AT ALL, deliberately. With no locks this
 * returns null on every input and the run behaves exactly as it did before this
 * feature existed — the solver's own infeasibility path is unchanged and still
 * owns every message it used to own.
 *
 * Returns null when there is nothing to say, otherwise { tone, text }: "note"
 * for the everything-is-locked case, which is an answer and not a failure.
 *
 * @param openCount how many slots THIS RUN would try to fill.
 */
export function lockPreflight({ plan, rosterSlots, openCount }) {
  const locked = lockedSlotIds(plan, rosterSlots);

  const spend = lockedSpendOf(plan);
  const budget = Number(plan && plan.budget) || 0;
  const n = `${locked.length} lock${locked.length === 1 ? "" : "s"}`;

  // 🔴 THE SLOT-MODE ARM RUNS EVEN WITH NO LOCKS, which is why it sits above
  // the early return. Under budgetMode "slot" the rail figures are a floor on
  // what the plan must cost, so a set of earmarks can exceed the total budget
  // on their own — no lock required. The solver would find that infeasible and
  // say so per position; this says it once, in money, before the run.
  if (planBudgetMode(plan) === "slot") {
    const buckets = (plan && plan.buckets) || {};
    // 🔴 COMMITTED, NOT LOCKED. `spend` above is lock spend; every slot holding
    // a primary is money this plan owes whether or not it is locked, and a
    // preflight that counted only the locked ones would clear a plan the solver
    // then rejects.
    let committedSpend = 0;
    let earmarked = 0;
    let bare = 0;
    (rosterSlots || []).forEach((slot) => {
      const primary = primaryOf(buckets[slot.id]);
      if (primary) { committedSpend += Number(primary.price) || 0; return; }
      const raw = Number(buckets[slot.id] && buckets[slot.id].budget);
      if (Number.isFinite(raw) && raw > 0) earmarked += Math.floor(raw);
      else bare += 1;
    });
    const need = committedSpend + earmarked + bare;
    if (need > budget) {
      return {
        tone: "error",
        text: `Your spot budgets total ${money(earmarked)}, and with ${money(committedSpend)} committed and ${money(bare)} for the ${bare} spot${bare === 1 ? "" : "s"} you have not budgeted, that needs ${money(need)} against a ${money(budget)} budget — ${money(need - budget)} over. Lower a spot budget, raise the total, or switch back to your total budget.`,
      };
    }
  }

  if (locked.length === 0) return null;

  if (spend > budget) {
    return {
      tone: "error",
      text: `Your ${n} total ${money(spend)} against a ${money(budget)} budget — ${money(spend - budget)} over. Unlock one, or raise the budget.`,
    };
  }
  // Not an error: a fully locked roster is a finished draft, and the honest
  // answer is that there is nothing left to decide.
  if (openCount === 0) {
    return { tone: "note", text: `Every slot is locked — nothing to optimize.` };
  }
  const left = budget - spend;
  if (left < openCount) {
    return {
      tone: "error",
      text: `Your ${n} leave ${money(left)} for ${openCount} unfilled ${openCount === 1 ? "spot" : "spots"} — every spot needs at least $1. Unlock one, or raise the budget.`,
    };
  }
  return null;
}

/**
 * The hook. Returns the handler the toolbar calls and whatever went wrong.
 */
export default function useOptimizeActions({
  activePlan,
  rosterSlots,
  dynamicPrices,
  settings,
  getPlayerProj,
  getPlayerGames,
  getPlayerVorp,
  createFilledPlans,
  priceSheet,
  ppgOverrides,
}) {
  const [optimizeError, setOptimizeError] = useState(null);

  // 🔴 ONE POOL, read by the solver, the cost badges AND the fingerprint. The
  // fingerprint intersects your sheets with it (see planFingerprint), so a pool
  // built differently here than there would make a price you set count for the
  // warning and not for the solve, or the reverse.
  const pool = useMemo(() => poolFor(settings), [settings]);
  const poolKeys = useMemo(() => new Set(pool.map((p) => `${p.name}-${p.pos}`)), [pool]);

  /**
   * 🔴 RECOMPUTED EVERY RENDER, COMPARED AGAINST WHAT THE LAST SOLVE STORED.
   * This is the whole of "when should I press Rebuild": the button reads it,
   * and nothing acts on it. Auto-rebuilding on a mismatch would throw away the
   * plan on screen the moment you typed a price — the user asked to be told,
   * not overruled.
   */
  const fingerprintOf = useCallback(
    (plan) => planFingerprint({ plan, rosterSlots, poolKeys, priceSheet, ppgSheet: ppgOverrides }),
    [rosterSlots, poolKeys, priceSheet, ppgOverrides]
  );
  const fingerprint = useMemo(() => fingerprintOf(activePlan), [fingerprintOf, activePlan]);
  const storedFingerprint = (activePlan && activePlan.optimized && activePlan.optimized.fingerprint) || null;
  // Three states, not two. A hand-built plan and a plan generated before
  // fingerprints existed BOTH have nothing stored, and neither may be called
  // stale OR optimal — there is no claim to make about them.
  const isStale = !!storedFingerprint && storedFingerprint !== fingerprint;
  const isFresh = !!storedFingerprint && storedFingerprint === fingerprint;
  // Separate from the error, because "every slot is locked" is an ANSWER. It
  // renders muted rather than under "Could not optimize:", which would call a
  // finished draft a failure.
  const [optimizeNote, setOptimizeNote] = useState(null);

  /**
   * Run the solver twice — once for points per game, once for VORP — and turn
   * each result into its own plan.
   *
   * 🔴 "OPTIMIZE REMAINING", NOT "OPTIMIZE". It only fills slots whose
   * primaryOf is null; anything you already committed to is left exactly as it
   * is, and its price comes off the budget first. The label must not promise to
   * reconsider your existing picks.
   *
   * Note "open" is primaryOf === null, NOT "cell 0 is empty": a slot whose
   * cell 0 was removed but which still holds a backup is already working, so
   * the solver leaves it alone. That is the same fall-through the budget uses.
   */
  const handleOptimize = useCallback(({ keepCommitments = false, fromScratch = false } = {}) => {
    // Mutually exclusive by definition: one holds every commitment, the other
    // holds none. Throw rather than silently pick, because picking wrong would
    // produce a plausible plan that answers the opposite question.
    if (keepCommitments && fromScratch) {
      throw new Error("useOptimizeActions: keepCommitments and fromScratch are mutually exclusive.");
    }
    // The identity a re-run replaces on, with the objective. Derived from the
    // flags rather than passed, so a new mode cannot forget to name itself and
    // silently start clobbering another mode's output.
    const mode = fromScratch ? "scratch" : (keepCommitments ? "commitments" : "remaining");
    const objective = planObjective(activePlan);
    setOptimizeError(null);
    setOptimizeNote(null);
    if (!activePlan) return;

    // 🔴 BEFORE THE SOLVE, so a refusal leaves the plan byte-identical. The
    // open count is mode-dependent and that matters: "Rebuild everything"
    // reopens every unlocked slot, while "Fill empty spots" only ever touches
    // the ones with nobody in them. Counting the wrong set would refuse runs
    // that are perfectly feasible.
    const lockedIds = lockedSlotIds(activePlan, rosterSlots);
    const openCount = fromScratch
      ? rosterSlots.length - lockedIds.length
      : rosterSlots.filter((s) => !primaryOf(activePlan.buckets[s.id])).length;
    const pre = lockPreflight({ plan: activePlan, rosterSlots, openCount });
    if (pre) {
      if (pre.tone === "note") setOptimizeNote(pre.text);
      else setOptimizeError(pre.text);
      return;
    }

    // 🔴 THE PRICE MAP IS BUILT FROM THE REAL PLAN EVEN WHEN THE SOLVER IS HANDED
    // AN EMPTY ONE. Those are separate arguments and that separation IS the
    // fromScratch trick: no slot is held, so any player can be dropped, but the
    // prices you set are still the prices everyone pays.
    const priceOf = makePriceOf(dynamicPrices, priceSheet);
    // 🔴 lockedPlanOf, NOT `{ buckets: {} }`. "Rebuild everything" drops every
    // pick you made — that is the mode — but a lock is not a pick, it is a
    // purchase, and re-deciding a slot you have already paid for produces a
    // plan you cannot execute on a budget you no longer have.
    const solvePlan = fromScratch ? lockedPlanOf(activePlan) : activePlan;

    // 🔴 BOTH objectives honour your PPG overrides. VORP shifts by the same
    // delta rather than ignoring them — see makePlayerVorp for why an
    // approximation beats one build silently disagreeing with the other.
    //
    // 🔴 AND BOTH ARE IN SEASON POINTS. `ppgOf` is the RATE, for the totals the
    // user reads; `seasonOf` is rate × games, which is what gets maximised. They
    // were the same function when the rate was season/17 — a constant scaling,
    // harmless to a solver. Per-player games is not constant, so optimising the
    // rate would trade season points for availability. Keep them apart.
    const seasonOf = makePlayerSeasonPoints(getPlayerProj, getPlayerGames, ppgOverrides);
    const vorpOf = makePlayerVorp(getPlayerProj, getPlayerGames, getPlayerVorp, ppgOverrides);
    // 🔴 BOTH SOLVES ALWAYS RUN, even though only one becomes the plan. The
    // second one is not waste: it is the entire basis of the agreement note,
    // which exists because the two objectives coincide often enough that a
    // toggle producing an identical roster reads as a broken switch. Each solve
    // is ~7ms.
    const runs = [
      { objective: "PPG", key: "ppg", valueOf: seasonOf },
      { objective: "VORP", key: "vorp", valueOf: vorpOf },
    ].map((r) => ({
      ...r,
      // Four ranks: A honours your commitments, B-D are whole rosters built
      // from what A did not take.
      multi: optimizePlans({
        plan: solvePlan, rosterSlots, pool, priceOf, valueOf: r.valueOf, budget: activePlan.budget,
        // The plan's own setting, never an argument — the same rule the
        // objective follows, and for the same reason.
        budgetMode: planBudgetMode(activePlan),
      }, { ranks: BUCKET_WIDTH, keepCommitments }),
    })).map((r) => ({ ...r, result: r.multi.ranks[0] || { ok: false, reason: r.multi.stoppedReason } }));

    // 🔴 THE PLAN'S OBJECTIVE DECIDES, NOT AN ARGUMENT. Both buttons run this
    // function and neither passes an objective — that is the toolbar split, and
    // it is why a new scope can never again need a new pair of buttons.
    const chosen = runs.find((r) => r.key === objective) || runs[0];
    const other = runs.find((r) => r !== chosen);

    // 🔴 ONLY THE CHOSEN RUN CAN FAIL THE CLICK. The other one failing costs the
    // comparison sentence and nothing else, so it must not produce an error
    // about a plan the user did not ask for — which the old both-objectives
    // code did, and which read as "your optimize failed" on a run that worked.
    if (!chosen.multi.ranks.length) {
      // Surfaced inline, never as an alert and never silently: a button that
      // appears to do nothing is worse than one that says why it did nothing.
      setOptimizeError(chosen.multi.stoppedReason || "no roster could be built");
      return;
    }
    // A short chain is information, not an error: three columns beat a failure.
    if (chosen.multi.ranks.length < BUCKET_WIDTH) {
      setOptimizeError(`Only ${chosen.multi.ranks.length} of ${BUCKET_WIDTH} columns could be built — ${chosen.multi.stoppedReason}`);
    }

    // Only from-scratch can drop a player you placed, so only it has an absence
    // worth narrating. The other modes hold what you placed by construction.
    const changed = fromScratch
      ? changeNote(activePlan, chosen.result.assignments, rosterSlots)
      : null;
    const comparable = other && other.multi.ranks.length > 0;
    const agreement = comparable
      ? agreementNote(chosen.result.assignments, other.result.assignments, chosen.objective, other.objective)
      : null;
    const differs = comparable
      ? differingSlots(chosen.result.assignments, other.result.assignments)
      : [];

    // 🔴 A CELL THE SOLVER PRICED FROM YOUR SHEET IS MARKED AS YOURS. priceOf
    // reads the sheet first, so that cell already holds your number — and an
    // un-edited cell is exactly what resyncMarket overwrites with the board on
    // the next dial move or plan switch. Without this the generated plan
    // silently stops agreeing with the sheet that produced it, and its
    // fingerprint moves for a plan nobody touched.
    const fromSheet = (player) => priceSheet && priceSheet[`${player.name}-${player.pos}`] !== undefined;
    const columns = chosen.multi.ranks.map(
      (r) => r.assignments.map((a) => ({ ...a, edited: fromSheet(a.player) }))
    );

    // 🔴 FIRED HERE, ON THE RUN THAT PRODUCED PLANS — not at the top of
    // handleOptimize. Every refusal above this line (the mutually-exclusive
    // throw, the missing plan, the lock preflight) returns without touching
    // the plan, and a refused run is not an optimize.
    //
    // ⚠️ slots_filled COUNTS PLAN A's ASSIGNMENTS, not all four columns. One
    // press produces four ranked builds of the SAME roster; four is a constant
    // of the feature and would tell us nothing, while Plan A's count is the
    // number of slots the solver actually filled — and a 0 here is the silent
    // no-op (planOptimizer returns ok with an empty assignments array when
    // nothing is open) reaching a real user.
    capturePlanOptimized({
      mode,
      objective: chosen.objective,
      slotsFilled: Array.isArray(chosen.result?.assignments) ? chosen.result.assignments.length : null,
      lockedSlots: Array.isArray(lockedIds) ? lockedIds.length : null,
    });

    createFilledPlans([{
      nameSuffix: `(${chosen.objective}, ${SCOPE_WORD[mode] || mode})`,
      columns,
      optimized: {
        mode,
        objective: chosen.objective,
        differs,
        lockedSlots: lockedIds,
        // 🔴 STAMPED BY createFilledPlans, ON THE PLAN IT BUILDS. The
        // fingerprint hashes every target's price and this run is about to
        // replace those targets, so a value computed here — before the write —
        // would describe the plan we started from. It was exactly that, once,
        // and the button would have been born claiming "prices changed".
        fingerprint: null,
        note: [changed, agreement].filter(Boolean).join(" ") || null,
      },
    }], 0, fingerprintOf);
  }, [activePlan, rosterSlots, dynamicPrices, pool, fingerprintOf, getPlayerProj, getPlayerGames, getPlayerVorp, createFilledPlans, priceSheet, ppgOverrides]);

  const optimizeRemaining = useCallback(() => handleOptimize(), [handleOptimize]);

  /**
   * Same solve, but EVERY column keeps your committed primaries instead of only
   * Plan A. So B/C/D become alternatives that still contain your guys, rather
   * than four answers to "what if you lose them". Hand-set prices are fixed
   * throughout, same as the default run.
   *
   * 🔴 NO CALL SITE, DELIBERATELY — RETAINED, NOT DEAD. It shipped as
   * "Optimize around my commitments" and the button was cut from the toolbar:
   * its PLAN A is byte-identical to optimizeRemaining's, so the pair differed
   * only in whether B-D also excluded committed players, and a duplicated
   * primary column is what made the two buttons feel arbitrary next to each
   * other. Kept here — with keepCommitments and its tests in
   * planOptimizer.test.js and resolveAtMyPrices.test.js — so restoring it is
   * one line in DraftPlan's toolbar rather than a re-derivation. Do not delete
   * it to satisfy a dead-code sweep.
   */
  const optimizeAroundCommitments = useCallback(
    () => handleOptimize({ keepCommitments: true }),
    [handleOptimize]
  );

  /**
   * "Re-solve at my prices" — the ONLY mode that reconsiders a player you have
   * already committed to.
   *
   * 🔴 WHY IT EXISTS. Neither shipped button can drop a commitment:
   * optimizeRemaining skips committed slots by design, and
   * optimizeAroundCommitments keeps them by construction. So editing a
   * committed player's price moved the budget and nothing else — you could
   * raise Dak to $10 and he stayed. This solves EVERY slot from scratch while
   * still paying your hand-set prices, which is what "I changed a price,
   * re-optimize" means to the person clicking it.
   *
   * Lands as a NEW plan, like every other optimize output. A mode that CAN drop
   * your commitments must never overwrite the plan holding them.
   */
  const rebuildEverything = useCallback(
    () => handleOptimize({ fromScratch: true }),
    [handleOptimize]
  );

  /**
   * COST OF COMMITMENT, per slot: what does holding THIS player cost, on its
   * own, measured in starters PPG?
   *
   * 🔴 MEASURED AGAINST THE FULLY-FREE OPTIMUM, not against the build that
   * holds all your commitments. Against the latter every commitment costs zero
   * by construction — a number that looks fine and means nothing. So: solve
   * once with nothing committed (the ceiling), then once per committed slot
   * with THAT player forced in and everything else free. The gap is what that
   * one decision costs.
   *
   * 🔴 AND IT IS MEASURED AT BOARD PRICES FOR EVERYONE ELSE. It answers "what
   * would this cost if the rest of the market behaved", not "what will this
   * cost" — which matters more here than for the optimizer itself, because a
   * number to one decimal place looks far more certain than it is.
   *
   * Debounced: one solve per committed slot plus a baseline is ~120ms on a full
   * roster, which is fine on a pause and absurd on every keystroke.
   */
  const [commitmentCosts, setCommitmentCosts] = useState({});

  useEffect(() => {
    if (!activePlan || !rosterSlots || rosterSlots.length === 0) { setCommitmentCosts({}); return undefined; }

    const timer = setTimeout(() => {
      setCommitmentCosts(commitmentCostsFor({
        plan: activePlan,
        rosterSlots,
        pool,
        priceOf: makePriceOf(dynamicPrices, priceSheet),
        // Objective in season points; the cost it REPORTS in starters PPG.
        valueOf: makePlayerSeasonPoints(getPlayerProj, getPlayerGames, ppgOverrides),
        ppgOf: makePlayerPpg(getPlayerProj, getPlayerGames, ppgOverrides),
      }));
    }, COST_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [activePlan, rosterSlots, dynamicPrices, pool, getPlayerProj, getPlayerGames, priceSheet, ppgOverrides]);

  return {
    optimizeError, setOptimizeError,
    optimizeNote, setOptimizeNote,
    handleOptimize, optimizeRemaining, optimizeAroundCommitments, rebuildEverything,
    fingerprint, isStale, isFresh,
    commitmentCosts,
  };
}
